import React, { useEffect, useState } from 'react';
import { GenericModal, Modal } from '@rabbit/elements/shared-components';
import { FBD_Warranty, FBD_Warranty_Template_Public } from '@rabbit/data/types';
import {
  ShieldCheckIcon,
  DocumentTextIcon,
  CheckIcon,
  XMarkIcon,
  InformationCircleIcon,
  ClipboardDocumentListIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/solid';

interface WarrantyTermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  holdingLink: string;
}

interface WarrantyTermsSection {
  title: string;
  type: 'checks' | 'children';
  items?: {
    title: string;
    description: string;
    included: string;
  }[];
  children?: string | string[] | {
    title: string;
    description: string;
  }[];
}

const WarrantyTermsModal: React.FC<WarrantyTermsModalProps> = ({
  isOpen,
  onClose,
  holdingLink,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [termsData, setTermsData] = useState<WarrantyTermsSection[] | null>(
    null
  );

  useEffect(() => {
    const fetchWarrantyTerms = async () => {
      if (!isOpen || !holdingLink) return;

      setLoading(true);
      setError(null);

      try {
        // Step 1: Find warranty with matching holdingLink
        const warranties = await FBD_Warranty.query()
          .where('holdingLink', '==', holdingLink)
          .getContents();

        if (warranties.length === 0) {
          setError('No warranty found for this product');
          setLoading(false);
          return;
        }

        // Get the latest warranty
        const latestWarranty = warranties.reduce((latest, current) =>
          latest.tcreate > current.tcreate ? latest : current
        );

        // Step 2: Get the templateLink from the warranty
        const templateLink = latestWarranty.templateLink;

        if (!templateLink) {
          setError('No template link found for this warranty');
          setLoading(false);
          return;
        }

        // Step 3: Get the warranty template public data
        const templatePublic = await FBD_Warranty_Template_Public.get(
          templateLink
        );

        if (!templatePublic) {
          setError('Warranty template not found');
          setLoading(false);
          return;
        }

        // Step 4: Get the terms data
        // Check both info and terms fields for warranty terms data
        const termsSource =
          templatePublic.terms && templatePublic.terms.length > 0
            ? templatePublic.terms
            : templatePublic.info;

        if (termsSource && termsSource.length > 0) {
          // Transform the data to match the expected format
          const transformedTerms = termsSource
            .map((section) => {
              if (!section) return null;

              const title = section.title || '';

              // Handle 'checks' type with items array
              if (section.type === 'checks' && Array.isArray(section.items)) {
                return {
                  title,
                  type: 'checks',
                  items: section.items.map((item) => ({
                    title: item?.title || '',
                    description: item?.description || '',
                    included: item?.included || 'yes',
                  })),
                };
              }
              // Handle sections with 'children' property (string, array of strings, or array of objects)
              else if ((section as any).children !== undefined) {
                // Check if children is an array of objects with title and description
                const children = (section as any).children;
                if (Array.isArray(children) && children.length > 0 && typeof children[0] === 'object' && children[0] !== null) {
                  // Keep the original structure if it's an array of objects
                  return {
                    title,
                    type: 'children',
                    children: children,
                  };
                } else {
                  // Handle string or array of strings
                  return {
                    title,
                    type: 'children',
                    children: children, // This can be a string or string[]
                  };
                }
              }
              // Handle sections with items array but not 'checks' type
              else if (Array.isArray(section.items)) {
                return {
                  title,
                  type: 'children',
                  children: section.items.map(
                    (item) => item?.description || item?.title || ''
                  ),
                };
              } else {
                // Fallback for invalid section structure
                return {
                  title,
                  type: 'children',
                  children: 'No details available',
                };
              }
            })
            .filter(Boolean) as WarrantyTermsSection[];

          console.log('Transformed terms data:', transformedTerms);
          setTermsData(transformedTerms);
        } else {
          setError('No warranty terms found in template');
          console.log('Template public data:', templatePublic);
        }
      } catch (err) {
        console.error('Error fetching warranty terms:', err);
        setError('Failed to load warranty terms');
      } finally {
        setLoading(false);
      }
    };

    fetchWarrantyTerms();
  }, [isOpen, holdingLink]);

  const renderChecksSection = (section: WarrantyTermsSection) => (
    <div>
      <h3 className="font-nunito text-primary-700 mb-3 flex items-center gap-1 text-lg font-semibold">
        <ClipboardDocumentListIcon className="h-4 w-4" />
        {section.title}
      </h3>
      <div className="space-y-3">
        {section.items?.map((item, idx) => (
          <div
            key={idx}
            className="rounded-md border-b p-1 pb-2 transition-colors hover:bg-gray-50"
          >
            <div className="flex items-center justify-between">
              <h4 className="font-nunito flex items-center gap-1 text-sm font-medium text-gray-800">
                {item.title}
              </h4>
              <span
                className={`font-nunito flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                  item.included === 'yes'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {item.included === 'yes' ? (
                  <>
                    <CheckIcon className="h-2.5 w-2.5" />
                    Included
                  </>
                ) : (
                  <>
                    <XMarkIcon className="h-2.5 w-2.5" />
                    Not Included
                  </>
                )}
              </span>
            </div>
            <p className="font-nunito mt-1 text-sm text-gray-600">{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderChildrenSection = (section: WarrantyTermsSection) => (
    <div>
      {section.title && (
        <h3 className="font-nunito text-primary-700 mb-3 flex items-center gap-1 text-lg font-semibold">
          <ShieldCheckIcon className="h-4 w-4" />
          {section.title}
        </h3>
      )}

      {typeof section.children === 'string' ? (
        <div
          dangerouslySetInnerHTML={{ __html: section.children }}
          className="font-nunito rounded-md border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700"
        />
      ) : Array.isArray(section.children) && section.children.length > 0 && typeof section.children[0] === 'object' ? (
        <ul className="space-y-3">
          {section.children.map((item: any, idx) => (
            <li
              key={idx}
              className="font-nunito rounded-md border-b p-1 pb-2 transition-colors hover:bg-gray-50"
            >
              <h4 className="font-nunito flex items-center gap-1 text-sm font-medium text-gray-800">
                {item.title}
              </h4>
              <p className="font-nunito mt-1 text-sm text-gray-600">{item.description}</p>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="space-y-2 pl-4">
          {section.children?.map((item, idx) => (
            <li
              key={idx}
              className="font-nunito flex items-start gap-1 rounded-md p-1 text-sm text-gray-700 transition-colors hover:bg-gray-50"
            >
              <InformationCircleIcon className="text-primary-600 mt-0.5 h-4 w-4 shrink-0" />
              {typeof item === 'object' && item !== null ? (
                <div className="font-nunito">
                  <div className="text-sm font-medium">{item.title}</div>
                  {item.description && <div className="text-sm text-gray-600">{item.description}</div>}
                </div>
              ) : (
                <span className="font-nunito text-sm">{item}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <Modal
      kind="generic"
      isFullScreen={true}
      className="w-full max-w-[729px] bg-white"
      settings={{
        title: "Warranty Terms",
        handleClose: onClose,
      }}
    >
      <div className="font-nunito p-4">
        {loading && (
          <div className="flex h-32 items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <div className="border-primary-600 h-8 w-8 animate-spin rounded-full border-b-2"></div>
              <p className="font-nunito text-sm text-gray-600">Loading warranty terms...</p>
            </div>
          </div>
        )}

        {error && !loading && (
          <div className="flex flex-col items-center justify-center gap-2 rounded-md bg-red-50 p-4 text-center">
            <ExclamationCircleIcon className="h-6 w-6 text-red-500" />
            <div className="font-nunito text-sm font-medium text-red-600">{error}</div>
          </div>
        )}

        {!loading && !error && termsData && termsData.length > 0 ? (
          <div className="max-h-[80vh] space-y-4 pr-2">
            {termsData.map((section, idx) => (
              <div
                key={idx}
                className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm"
              >
                {section.type === 'checks' &&
                section.items &&
                section.items.length > 0
                  ? renderChecksSection(section)
                  : renderChildrenSection(section)}
              </div>
            ))}
          </div>
        ) : !loading && !error ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-md bg-gray-50 p-4 text-center">
            <InformationCircleIcon className="h-6 w-6 text-gray-400" />
            <div className="font-nunito text-sm font-medium text-gray-600">
              No warranty terms available
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default WarrantyTermsModal;
